'use client';

import Image from 'next/image';
import { Marquee } from '@/components/ui/marquee';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import { MainHeader } from './main-header';

export function Highlight({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'bg-[#29ac9692] p-1 py-0.5 font-bold text-[#00FFD3] dark:bg-[#f0abfc] dark:text-[#d946ef] rounded-sm',
        className
      )}
    >
      {children}
    </span>
  );
}

export interface TestimonialCardProps {
  name: string;
  role: string;
  img?: string;
  description: React.ReactNode;
  className?: string;
  [key: string]: any;
}

export function TestimonialCard({
  description,
  name,
  img,
  role,
  className,
  ...props // Capture the rest of the props
}: TestimonialCardProps) {
  return (
    <div
      className={cn(
        'mb-4 flex w-full cursor-pointer break-inside-avoid flex-col items-center justify-between gap-6 rounded-xl p-4',
        // light styles
        // ' border border-neutral-200 bg-white',
        // dark styles
        'bg-brand-third [border:1px_solid_rgba(255,255,255,.1)] [box-shadow:0_-20px_80px_-20px_#ffffff1f_inset]',
        className
      )}
      {...props}
    >
      <div className='select-none text-sm font-normal text-neutral-700 dark:text-neutral-400'>
        {description}
        <div className='flex flex-row py-1'>
          <Star className='size-3 fill-yellow-500 text-yellow-500' />
          <Star className='size-3 fill-yellow-500 text-yellow-500' />
          <Star className='size-3 fill-yellow-500 text-yellow-500' />
          <Star className='size-3 fill-yellow-500 text-yellow-500' />
          <Star className='size-3 fill-yellow-500 text-yellow-500' />
        </div>
      </div>

      <div className='flex w-full select-none items-center justify-start gap-5'>
        {/* <Image
          width={40}
          height={40}
          src={img || ''}
          alt={name}
          className='size-10 rounded-full ring-1 ring-border ring-offset-1'
        /> */}

        <div>
          <p className='font-medium text-neutral-500'>{name}</p>
          <p className='text-xs font-normal text-neutral-400'>{role}</p>
        </div>
      </div>
    </div>
  );
}

const testimonials = [
  {
    name: 'Arjun Nair',
    role: 'Frontend Developer at InnovateTech',
    img: 'https://randomuser.me/api/portraits/men/91.jpg',
    description: (
      <p>
        Debug Media played a crucial role in enhancing my frontend skills.
        <Highlight>It provided a strong foundation in React and Next.js</Highlight>, helping me
        build scalable web applications.
      </p>
    ),
  },
  {
    name: 'Habil',
    role: 'Frontend Developer at Infotech',
    img: 'https://randomuser.me/api/portraits/men/91.jpg',
    description: (
      <p>
        I enrolled in MERN STACK Course
        <Highlight>And I got a package of 30 LPA</Highlight>, I'm very grateful to Debug Media.
      </p>
    ),
  },
  {
    name: 'Neha Menon',
    role: 'UI/UX Specialist at CreativeLabs',
    img: 'https://randomuser.me/api/portraits/women/75.jpg',
    description: (
      <p>
        The DM course covered every essential frontend topic.
        <Highlight>From HTML and CSS to React state management</Highlight>, it gave me confidence to
        tackle real-world projects.
      </p>
    ),
  },
  {
    name: 'Aisha Mohammed',
    role: 'Frontend Engineer at NexaSoft',
    img: 'https://randomuser.me/api/portraits/women/62.jpg',
    description: (
      <p>
        Debug Media’s hands-on approach made a huge difference.
        <Highlight>Working on real React and Next.js projects</Highlight> helped me solidify my
        understanding.
      </p>
    ),
  },
  {
    name: 'Rahul Pillai',
    role: 'Full Stack Developer at TechVision',
    img: 'https://randomuser.me/api/portraits/men/45.jpg',
    description: (
      <p>
        DM’s frontend lessons were well-structured and practical.
        <Highlight>It made working with React hooks and state management</Highlight> much clearer
        and easier.
      </p>
    ),
  },
  {
    name: 'Maria Thomas',
    role: 'Frontend Designer at Innovix Solutions',
    img: 'https://randomuser.me/api/portraits/women/48.jpg',
    description: (
      <p>
        Debug Media’s course gave me a deeper understanding of frontend principles.
        <Highlight>Mastering CSS and responsive design</Highlight> became second nature to me.
      </p>
    ),
  },
  {
    name: 'Ibrahim Khan',
    role: 'MERN Stack Developer at CloudCore',
    img: 'https://randomuser.me/api/portraits/men/34.jpg',
    description: (
      <p>
        DM’s MERN stack content was incredibly insightful.
        <Highlight>Learning React, Node.js, and MongoDB</Highlight> gave me the skills to develop
        full-fledged web apps.
      </p>
    ),
  },
  {
    name: 'Vishnu Ramesh',
    role: 'React Developer at NextGen Solutions',
    img: 'https://randomuser.me/api/portraits/men/33.jpg',
    description: (
      <p>
        Debug Media’s focus on React fundamentals was exceptional.
        <Highlight>
          It clarified key concepts like props, state, and lifecycle methods
        </Highlight>, making development smoother.
      </p>
    ),
  },
  {
    name: 'Roshan Joseph',
    role: 'Frontend Engineer at BrightTech',
    img: 'https://randomuser.me/api/portraits/men/81.jpg',
    description: (
      <p>
        The DM course made frontend frameworks easy to grasp.
        <Highlight>It provided clear guidance on building responsive interfaces</Highlight> with
        modern tools.
      </p>
    ),
  },
  {
    name: 'Fatima Salim',
    role: 'UI Developer at SynergyTech',
    img: 'https://randomuser.me/api/portraits/women/57.jpg',
    description: (
      <p>
        Debug Media’s emphasis on JavaScript fundamentals was invaluable.
        <Highlight>Understanding DOM manipulation and event handling</Highlight> became seamless.
      </p>
    ),
  },
  {
    name: 'Jeevan Kurian',
    role: 'Frontend Lead at SoftCore',
    img: 'https://randomuser.me/api/portraits/men/67.jpg',
    description: (
      <p>
        DM's approach to teaching frontend technologies stood out.
        <Highlight>Concepts like SSR, SSG and API integration</Highlight> were explained with
        clarity and precision.
      </p>
    ),
  },
];

function VerticalScrollTestimonials() {
  return (
    <section id='testimonials' className='container py-10'>
      {/* <h2 className='mb-4 text-center text-5xl font-bold leading-[1.2] tracking-tighter text-foreground'>
        What my students are saying
      </h2>
      <h3 className='mx-auto mb-8 max-w-lg text-balance text-center text-lg font-medium tracking-tight text-foreground/80'>
        Find out what{' '}
        <span className='bg-gradient bg-clip-text text-transparent'>our students</span> say about
        our courses and mentoring
      </h3> */}
      <MainHeader
        color='#19FFAC'
        miniTitle='Testimonials'
        subTitle='What my students say'
        description='Find out what our students say about our courses and mentoring'
      />
      <div className='relative mt-3 max-h-screen overflow-hidden'>
        <div className='gap-4 md:columns-2 xl:columns-3 2xl:columns-4'>
          {Array(Math.ceil(testimonials.length / 3))
            .fill(0)
            .map((_, i) => (
              <Marquee
                vertical
                key={i}
                className={cn({
                  '[--duration:60s]': i === 1,
                  '[--duration:30s]': i === 2,
                  '[--duration:70s]': i === 3,
                })}
              >
                {testimonials.slice(i * 3, (i + 1) * 3).map((card, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{
                      delay: Math.random() * 0.8,
                      duration: 1.2,
                    }}
                  >
                    <TestimonialCard {...card} />
                  </motion.div>
                ))}
              </Marquee>
            ))}
        </div>
        <div className='pointer-events-none absolute inset-x-0 bottom-0 h-1/4 w-full bg-gradient-to-t from-brand-third from-20%'></div>
        <div className='pointer-events-none absolute inset-x-0 top-0 h-1/4 w-full bg-gradient-to-b from-brand-third from-20%'></div>
      </div>
    </section>
  );
}

export default VerticalScrollTestimonials;
